export const YOUTUBE_MODES = {
    keyword: 'youtube.videos.discover_by_keyword',
    channel: 'youtube.videos.discover_by_channel',
    video: 'youtube.videos.collect_by_url',
} as const;
export type YoutubeMode = keyof typeof YOUTUBE_MODES;
export const isYoutubeCapability = (capability: string) =>
    Object.values(YOUTUBE_MODES).some(value => value === capability);

/** Keep in parity with Gateway youtube-target/v1. No redirects or playlist expansion. */
export function youtubeTarget(value: string, mode: Exclude<YoutubeMode, 'keyword'>): string {
    if (value.length > 2048 || value !== value.trim()) throw new Error('Invalid YouTube target.');
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password || url.port || url.hash
        || !['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be'].includes(url.hostname))
        throw new Error('Invalid YouTube target.');
    if (mode === 'channel') {
        if (url.hostname === 'youtu.be' || url.search || !/^\/(?:channel\/UC[A-Za-z0-9_-]{22}|@[A-Za-z0-9_.-]{3,100})\/?$/.test(url.pathname))
            throw new Error('Expected a public YouTube channel URL.');
        return 'https://www.youtube.com' + url.pathname.replace(/\/$/, '');
    }
    const allowed = ['v', 't', 'start', 'si', 'feature'];
    if ([...url.searchParams.keys()].some(key => !allowed.includes(key)) || url.searchParams.getAll('v').length > 1)
        throw new Error('Expected one video, not a playlist.');
    const id = url.hostname === 'youtu.be' ? url.pathname.slice(1).replace(/\/$/, '')
        : url.pathname === '/watch' ? url.searchParams.get('v')
            : /^\/(shorts|live)\/[^/]+\/?$/.test(url.pathname) ? url.pathname.split('/')[2] : null;
    if (!id || !/^[A-Za-z0-9_-]{11}$/.test(id) || (url.pathname !== '/watch' && url.searchParams.has('v')))
        throw new Error('Expected a public YouTube video URL.');
    return 'https://www.youtube.com/watch?v=' + id;
}

export const youtubeUrlInputs = (text: string) => text.split(/\r?\n/).map(value => value.trim()).filter(Boolean);
export function youtubeTargets(text: string, mode: YoutubeMode): string[] {
    if (mode === 'keyword') return [];
    if (!['channel', 'video'].includes(mode)) throw new Error('Invalid YouTube mode.');
    const inputs = youtubeUrlInputs(text);
    if (!inputs.length || inputs.length > 20) throw new Error('Expected 1–20 YouTube URLs.');
    return [...new Set(inputs.map(value => youtubeTarget(value, mode)))];
}
